"use client"

import React, { ReactNode, useEffect, useMemo, useRef, useTransition } from "react"
import Image from "next/image"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { SEARCH_PARAMS_FF_SEARCH_PARAMS, SEARCH_PARAMS_PAGE_SIZE } from "@/constants/ui"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { CheckIcon } from "@radix-ui/react-icons"
import { Campaign, NavigationParams, Question, SearchParams } from "@ub/fact-finder-client"
import { CrossIcon, LoaderCircleIcon, XIcon } from "lucide-react"
import { useIsMounted } from "usehooks-ts"

import {
  trackGuidedSellingClick,
  trackGuidedSellingImpression,
  trackGuidedSellingResult,
  trackGuidedSellingStart,
} from "@/lib/tracking/tracking-helper"
import { decodeObj, encodeObj } from "@/lib/urlObject"
import { Button, RadioGroup, RadioGroupItem } from "@/components/ui/components"
import { cn } from "@/components/ui/helpers/utils"

interface GuidedSellingContainerProps extends React.HTMLProps<HTMLDivElement> {
  campaign: Campaign
  onAnswerClick: (searchParams: SearchParams) => void
  onResetClick: () => void
  label_breadcrumb_start: string
  label_breadcrumb_end: string
  label_from_to: string
  label_or: string
  label_btn_confirm: string
  label_btn_reset: string
  label_btn_open: string
  blok_summary_top: ReactNode
  blok_summary_bottom: ReactNode
  showLoadingOnButton: boolean
  showCloseButton: boolean
  defaultClosed: boolean
  onClosedChange?: (closed: boolean) => void
  onSummaryPageChange?: (isSummaryPage: boolean) => void
  trackingPageType: "landingpage" | "search" | "category" | "brand" | "__unknown"
  ffSearchParams: NavigationParams | SearchParams | undefined
}

export function GetCampaignNameForTracking(campaign: Campaign | undefined): string {
  return campaign?.name ?? campaign?.id ?? "__unknown"
}

export function GetCategoryNameForTracking(searchParams: NavigationParams | SearchParams | null | undefined): string {
  if (!searchParams) {
    return ""
  }

  const categoryFilter = searchParams.filters?.find((filter) => filter.name === "category")
  if (
    categoryFilter &&
    categoryFilter.values.length > 0 &&
    categoryFilter.values[0].value instanceof Array &&
    categoryFilter.values[0].value.length > 0
  ) {
    return categoryFilter.values[0].value[categoryFilter.values[0].value.length - 1] ?? ""
  }

  return ""
}

export const GuidedSellingContainer: React.FC<
  Omit<GuidedSellingContainerProps, "onAnswerClick" | "onResetClick" | "showLoadingOnButton"> & { totalHits: number }
> = ({ campaign, totalHits, ...restProps }) => {
  const [loading, handle] = useTransition()
  const searchParams = useSearchParams()
  const router = useRouter()
  const isMounted = useIsMounted()
  const { setUsedGuidedSelling, setGuidedSellingCampaign } = useUbStorefrontState()

  function handleAnswerClick(answerParams: SearchParams) {
    const newSearchParams = new URLSearchParams(searchParams)

    if (!newSearchParams.has(SEARCH_PARAMS_PAGE_SIZE) && answerParams.hitsPerPage) {
      newSearchParams.set(SEARCH_PARAMS_PAGE_SIZE, answerParams.hitsPerPage.toString())
    }

    if (newSearchParams.has(SEARCH_PARAMS_FF_SEARCH_PARAMS)) {
      const ff = decodeObj(newSearchParams.get(SEARCH_PARAMS_FF_SEARCH_PARAMS) ?? "") as unknown as SearchParams
      if (ff) {
        ff.query = answerParams.query
        ff.advisorStatus = answerParams.advisorStatus
        newSearchParams.set(SEARCH_PARAMS_FF_SEARCH_PARAMS, encodeObj(ff))
      }
    } else if (answerParams.advisorStatus) {
      newSearchParams.set(SEARCH_PARAMS_FF_SEARCH_PARAMS, encodeObj({ advisorStatus: answerParams.advisorStatus }))
    }

    handle(() => router.push("?" + newSearchParams.toString()))
  }

  function resetWizard() {
    const newSearchParams = new URLSearchParams(searchParams)
    if (newSearchParams.has(SEARCH_PARAMS_FF_SEARCH_PARAMS)) {
      const ff = decodeObj(newSearchParams.get(SEARCH_PARAMS_FF_SEARCH_PARAMS) ?? "") as unknown as SearchParams
      if (ff && ff.advisorStatus) {
        delete ff.advisorStatus
        newSearchParams.set(SEARCH_PARAMS_FF_SEARCH_PARAMS, encodeObj(ff))
      }
    }
    handle(() => router.push("?" + newSearchParams.toString()))
  }

  useEffect(() => {
    if (isMounted() && totalHits > 0 && searchParams.has(SEARCH_PARAMS_FF_SEARCH_PARAMS)) {
      const ff = decodeObj(searchParams.get(SEARCH_PARAMS_FF_SEARCH_PARAMS) ?? "") as unknown as SearchParams
      if (ff && ff.advisorStatus != undefined) {
        const campaignName = GetCampaignNameForTracking(campaign)

        setUsedGuidedSelling(true)
        setGuidedSellingCampaign(campaignName)

        trackGuidedSellingResult(campaignName, "product_list", totalHits)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, totalHits, searchParams])

  return (
    <CustomGuidedSellingContainer
      campaign={campaign}
      onAnswerClick={handleAnswerClick}
      onResetClick={resetWizard}
      showLoadingOnButton={loading}
      {...restProps}
    />
  )
}

function getInnerTextFromHTML(htmlStr: string) {
  if (typeof window !== "undefined" && window.document) {
    const div = window.document.createElement("div")
    div.innerHTML = htmlStr
    return div.innerText
  } else {
    return htmlStr.replace(/<[^>]*>?/gm, "")
  }
}

interface contentEntry {
  slug: string
  title: string
  description: string
  image: string
  imageSizes: number[]
  longDescriptionHeader: string
  longDescriptionBody: string
}
function splitIntoContentEntry(entry: string): contentEntry {
  const splitEntry = entry
    .replace(/(?:<p[^>]*>)(.*)(?:<\/p[^>]*>)|(.*)/i, "$1$2")
    .split("|", 6)
    .map((s) => s.trim())
  return {
    slug: getInnerTextFromHTML(splitEntry[0] ?? ""),
    title: getInnerTextFromHTML(splitEntry[1] ?? ""),
    description: splitEntry[2] ?? "",
    image: getInnerTextFromHTML(splitEntry[3] ?? ""),
    imageSizes: getInnerTextFromHTML(splitEntry[3] ?? "")
      .replace(/.*_(\d+x\d+)px.*/i, "$1")
      .split("x", 1)
      .map((e) => Number.parseInt(e, 10))
      .map((n) => (isNaN(n) ? -1 : n)),
    longDescriptionHeader: getInnerTextFromHTML(splitEntry[4] ?? ""),
    longDescriptionBody: splitEntry[5] ?? "",
  }
}

export const CustomGuidedSellingContainer: React.FC<GuidedSellingContainerProps> = ({
  campaign,
  onAnswerClick,
  onResetClick,
  className,
  label_breadcrumb_start,
  label_breadcrumb_end,
  label_from_to,
  label_or,
  label_btn_confirm,
  label_btn_reset,
  label_btn_open,
  blok_summary_top,
  blok_summary_bottom,
  showLoadingOnButton,
  showCloseButton,
  defaultClosed,
  onClosedChange,
  onSummaryPageChange,
  trackingPageType,
  ffSearchParams,
  ...restProps
}) => {
  const [oldAnswers, setOldAnswers] = React.useState<{ [key: number]: string }>({})
  const [wizardLength, setWizardLength] = React.useState(1)
  const [selectedAnswer, setSelectedAnswer] = React.useState("")
  const [startTracked, setStartTracked] = React.useState<boolean>(false)
  const [closed, setClosed] = React.useState<boolean>(defaultClosed)
  const pathName = usePathname()
  const { usedGuidedSelling, setUsedGuidedSelling, guidedSellingCampaign, setGuidedSellingCampaign } =
    useUbStorefrontState()
  const isMounted = useIsMounted()
  const idToSearchParams = useMemo(
    () =>
      Object.fromEntries(
        campaign.activeQuestions?.flatMap((q) =>
          q.answers.map((a) => {
            return [a.searchParams.advisorStatus?.answerPath ?? "", a.searchParams]
          })
        ) ?? []
      ),
    [campaign.activeQuestions]
  )
  const containerDiv = useRef<HTMLDivElement>(null)

  const answeredQuestionsBreadcrumb = useMemo(() => {
    const treeWalkerLength = (questions: Question[]): number => {
      if (questions.length > 0) {
        return Math.min(
          ...questions.map((q) => {
            if (q.answers.length > 0) {
              return Math.min(
                ...q.answers.map((a) => {
                  if (a.questions) {
                    return treeWalkerLength(a.questions) + 1
                  }
                  return 1
                })
              )
            } else {
              return 0
            }
          })
        )
      } else {
        return 0
      }
    }
    const treeWalker = (
      questions: Question[],
      depth: number
    ): Array<{
      id: string
      question: contentEntry
      answer: contentEntry
      searchParams: SearchParams
      isTail: boolean
    }> => {
      return questions.flatMap((q) => {
        return q.answers.flatMap((a) => {
          if (a.selected) {
            setWizardLength(depth + 1 + treeWalkerLength(a.questions ?? []))
            return [
              {
                id: q.id,
                question: splitIntoContentEntry(q.text),
                answer: splitIntoContentEntry(a.text),
                searchParams: a.searchParams,
                isTail: a.questions === undefined || a.questions?.length === 0,
              },
            ]
          }
          if (a.questions) {
            const subCalResult = treeWalker(a.questions, depth + 1)
            if (subCalResult.length > 0) {
              return [
                {
                  id: q.id,
                  question: splitIntoContentEntry(q.text),
                  answer: splitIntoContentEntry(a.text),
                  searchParams: a.searchParams,
                  isTail: false,
                },
                ...subCalResult,
              ]
            }
          }
          return []
        })
      })
    }
    const result = treeWalker(campaign.advisorTree, 0)
    if (result.length === 0) {
      setWizardLength(treeWalkerLength(campaign.advisorTree))
    } else if (result.length > Object.keys(oldAnswers).length) {
      // if we get called fresh from an url, we have to rebuild the history of given answers
      setOldAnswers(Object.fromEntries(result.map((e, i) => [i, e.searchParams.advisorStatus?.answerPath ?? ""])))
    }
    return result
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.advisorTree])
  const isSummaryPage = useMemo(
    () =>
      answeredQuestionsBreadcrumb.length > 0 &&
      answeredQuestionsBreadcrumb[answeredQuestionsBreadcrumb.length - 1].isTail,
    [answeredQuestionsBreadcrumb]
  )
  useEffect(() => {
    onSummaryPageChange?.(isSummaryPage)
  }, [isSummaryPage, onSummaryPageChange])

  function handleCloseClick() {
    setClosed((prev) => {
      const next = !prev
      onClosedChange?.(next)
      return next
    })
  }
  useEffect(() => {
    if (
      oldAnswers[answeredQuestionsBreadcrumb.length] &&
      idToSearchParams[oldAnswers[answeredQuestionsBreadcrumb.length]]
    ) {
      setSelectedAnswer(oldAnswers[answeredQuestionsBreadcrumb.length])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idToSearchParams])

  useEffect(() => {
    // scroll into view after the first question was answered
    if (containerDiv.current && (answeredQuestionsBreadcrumb.length > 0 || Object.keys(oldAnswers).length > 0)) {
      containerDiv.current.scrollIntoView()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerDiv.current, answeredQuestionsBreadcrumb, oldAnswers])

  useEffect(() => {
    if (isMounted()) {
      console.debug(
        "Campaign Name:",
        GetCampaignNameForTracking(campaign),
        "Category:",
        GetCategoryNameForTracking(ffSearchParams),
        "Path:",
        pathName
      )
      trackGuidedSellingImpression(
        "guided_selling_element",
        GetCampaignNameForTracking(campaign),
        trackingPageType,
        campaign.category,
        pathName
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted])

  function setAnswerWithHistory(id: string) {
    setOldAnswers({ ...oldAnswers, [answeredQuestionsBreadcrumb.length]: id })
    return setSelectedAnswer(id)
  }

  function trackClick(cta_text: string, searchParams: SearchParams, callOnAnswer: boolean = true) {
    const campaignName = GetCampaignNameForTracking(campaign)
    const catName =
      GetCategoryNameForTracking(searchParams) != ""
        ? GetCategoryNameForTracking(searchParams)
        : GetCategoryNameForTracking(ffSearchParams)

    if (!usedGuidedSelling || guidedSellingCampaign != campaignName) {
      setUsedGuidedSelling(true)
      setGuidedSellingCampaign(campaignName)
    }

    trackGuidedSellingClick("guided_selling_element", campaignName, cta_text, trackingPageType, catName, pathName)

    if (answeredQuestionsBreadcrumb.length == 0 && !startTracked) {
      setStartTracked(true)
      trackGuidedSellingStart("guided_selling_element", "guided_selling_element_click", pathName)
    }
    if (callOnAnswer) {
      onAnswerClick(searchParams)
    }
  }

  if (!isSummaryPage && (!campaign.activeQuestions || campaign.activeQuestions?.length == 0)) {
    return null
  }

  return (
    <div className={cn("", className)} {...restProps} ref={containerDiv}>
      <div className={"flex flex-col gap-4 rounded-sm border border-gray-300 bg-white p-4"}>
        <div
          className={
            "-mx-4 -mt-4 grid h-4 grid-flow-col-dense gap-0.5 rounded-t-sm border-b border-gray-100 p-1 shadow-inner"
          }
        >
          {new Array(wizardLength).fill(0).map((_, i) => (
            <div
              key={i}
              className={cn("size-auto shrink grow bg-gray-300 first:rounded-l last:rounded-r", {
                "bg-primary-hover": i < answeredQuestionsBreadcrumb.length,
                "bg-primary-hover/65": i == answeredQuestionsBreadcrumb.length,
              })}
            ></div>
          ))}
        </div>
        {!closed && (
          <div className={"flex flex-row flex-nowrap justify-between border-b border-gray-100 pb-4"}>
            <div className="my-auto flex h-fit flex-row flex-wrap gap-2 text-xs leading-none">
              <button
                className={"flex cursor-pointer flex-row gap-x-1 hover:text-primary-hover hover:underline"}
                onClick={onResetClick}
                disabled={answeredQuestionsBreadcrumb.length === 0}
              >
                {label_breadcrumb_start}
              </button>
              <span className={"text-gray-500 last:hidden"}>{">"}</span>
              {answeredQuestionsBreadcrumb.length > 0 &&
                answeredQuestionsBreadcrumb.map((q, i) => (
                  <div className={"group flex flex-row gap-2"} key={q.id}>
                    <button
                      className={"flex cursor-pointer flex-row gap-x-1 hover:text-primary-hover hover:underline"}
                      onClick={
                        i == 0
                          ? onResetClick
                          : () => {
                              trackClick(q.question.slug, answeredQuestionsBreadcrumb[i - 1].searchParams)
                            }
                      }
                    >
                      {q.question.slug}
                    </button>
                    <span className={cn("text-gray-500", { "group-last:hidden": !q.isTail })}>{">"}</span>
                    {q.isTail && <span>{label_breadcrumb_end}</span>}
                  </div>
                ))}
            </div>
            <span className={"text-right text-nowrap"}>
              {label_from_to
                .replace(
                  "\$1",
                  (campaign.activeQuestions?.length
                    ? answeredQuestionsBreadcrumb.length + 1
                    : answeredQuestionsBreadcrumb.length
                  ).toString()
                )
                .replace("\$2", wizardLength.toString())}
            </span>
          </div>
        )}
        {!isSummaryPage &&
          campaign.activeQuestions?.map((q, i) => {
            const qContent = splitIntoContentEntry(q.text)
            if (closed && i > 0) {
              return null
            }
            return (
              <div className={"flex flex-col gap-4"} key={q.id}>
                <div className={"@container flex flex-col gap-6"}>
                  <div className={"flex flex-col gap-2"}>
                    <div
                      className={cn("flex flex-row gap-4", {
                        "justify-between": !closed,
                        "flex-wrap justify-start": closed,
                      })}
                    >
                      <span className={cn({ "text-3xl": !closed, "my-auto text-lg/tight font-bold": closed })}>
                        {(campaign.activeQuestions?.length ?? 0) > 1 &&
                          (i < 26
                            ? String.fromCharCode(i + 65)
                            : String.fromCharCode((i + 1) / 26 + 64) + ((i + 1) % 26).toString()) + ": "}
                        {qContent.title}
                      </span>
                      {showCloseButton && i === 0 && (
                        <Button
                          size={closed ? "smNoGrow" : "iconNoGrow"}
                          variant={closed ? "default" : "ghost"}
                          type={"button"}
                          onClick={handleCloseClick}
                        >
                          {closed ? label_btn_open : <XIcon className={"size-fit object-contain"} />}
                        </Button>
                      )}
                    </div>
                    {!closed && qContent.image && qContent.description && (
                      <div className={"flex flex-row flex-wrap gap-8"}>
                        <Image
                          className={"size-fit max-w-1/3 min-w-24 rounded-xl object-contain drop-shadow-xs"}
                          src={qContent.image}
                          alt={qContent.slug}
                          unoptimized={true}
                          width={qContent.imageSizes[0] ?? -1}
                          height={qContent.imageSizes[1] ?? -1}
                        />
                        <div className={"flex flex-col gap-2"}>
                          {qContent.description && (
                            <p
                              className={"max-w-prose hyphens-auto text-gray-600"}
                              dangerouslySetInnerHTML={{ __html: qContent.description }}
                            />
                          )}
                          {qContent.longDescriptionHeader && qContent.longDescriptionBody && (
                            <details className={"text-gray-800"}>
                              <summary>{qContent.longDescriptionHeader}</summary>
                              <p
                                className={"text-sm text-gray-600"}
                                dangerouslySetInnerHTML={{ __html: qContent.longDescriptionBody }}
                              />
                            </details>
                          )}
                          {q.answers.length == 2 &&
                            q.answers.filter((a) => {
                              const aContent = splitIntoContentEntry(a.text)
                              return (
                                aContent.title != "" &&
                                aContent.description == "" &&
                                aContent.image == "" &&
                                aContent.longDescriptionHeader == "" &&
                                aContent.longDescriptionBody == ""
                              )
                            }).length == 2 && (
                              <RadioGroup
                                className={"mt-6 grid w-fit grid-flow-row gap-2"}
                                value={selectedAnswer}
                                onValueChange={(value) => setAnswerWithHistory(value)}
                              >
                                {q.answers.map((a) => {
                                  const aContent = splitIntoContentEntry(a.text)
                                  const id = a.searchParams.advisorStatus?.answerPath ?? a.id

                                  return (
                                    <label
                                      className={"flex flex-row gap-3 rounded-sm p-1 align-middle hover:shadow-sm"}
                                      key={id}
                                      htmlFor={id + "checkbox"}
                                    >
                                      <div className={"aspect-square min-h-[30px] p-[3px]"}>
                                        <RadioGroupItem
                                          id={id + "checkbox"}
                                          value={id}
                                          className={`
                                            flex aspect-square size-[24px] items-center justify-center border-2 bg-white
                                            *:aspect-square *:h-[10px] *:shrink-0 *:grow-0 *:rounded-full *:bg-primary
                                            [&_svg]:hidden
                                          `}
                                          onClick={() => trackClick(aContent.title, a.searchParams, false)}
                                        />
                                      </div>
                                      <span className={cn("text-lg")}>{aContent.title}</span>
                                    </label>
                                  )
                                })}
                              </RadioGroup>
                            )}
                        </div>
                      </div>
                    )}
                    {!closed &&
                      !qContent.image &&
                      (qContent.description || (qContent.longDescriptionHeader && qContent.longDescriptionBody)) && (
                        <div className={"flex flex-col gap-2"}>
                          {qContent.description && (
                            <p
                              className={"hyphens-auto text-gray-600"}
                              dangerouslySetInnerHTML={{ __html: qContent.description }}
                            />
                          )}
                          {qContent.longDescriptionHeader && qContent.longDescriptionBody && (
                            <details className={"text-gray-800"}>
                              <summary>{qContent.longDescriptionHeader}</summary>
                              <p
                                className={"text-sm text-gray-600"}
                                dangerouslySetInnerHTML={{ __html: qContent.longDescriptionBody }}
                              />
                            </details>
                          )}
                        </div>
                      )}
                    {!closed && qContent.image && !qContent.description && (
                      <div className={"flex w-fit flex-col gap-2"}>
                        <Image
                          className={"min-w-24 rounded-sm object-contain drop-shadow-xs"}
                          src={qContent.image}
                          alt={qContent.slug}
                          unoptimized={true}
                          width={qContent.imageSizes[0] ?? -1}
                          height={qContent.imageSizes[1] ?? -1}
                        />
                        {qContent.longDescriptionHeader && qContent.longDescriptionBody && (
                          <details className={"text-gray-800"}>
                            <summary>{qContent.longDescriptionHeader}</summary>
                            <p
                              className={"text-sm text-gray-600"}
                              dangerouslySetInnerHTML={{ __html: qContent.longDescriptionBody }}
                            />
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                  {!closed &&
                    !(
                      q.answers.length == 2 &&
                      q.answers.filter((a) => {
                        const aContent = splitIntoContentEntry(a.text)
                        return (
                          aContent.title != "" &&
                          aContent.description == "" &&
                          aContent.image == "" &&
                          aContent.longDescriptionHeader == "" &&
                          aContent.longDescriptionBody == ""
                        )
                      }).length == 2
                    ) && (
                      <RadioGroup
                        className={"flex w-full flex-col flex-wrap gap-2 @sm:flex-row @xl:gap-4"}
                        value={selectedAnswer}
                        onValueChange={(value) => setAnswerWithHistory(value)}
                      >
                        {q.answers.map((a) => {
                          const aContent = splitIntoContentEntry(a.text)
                          const id = a.searchParams.advisorStatus?.answerPath ?? a.id
                          return (
                            <label
                              key={id}
                              htmlFor={id + "checkbox"}
                              className={`
                                relative flex min-h-[48px] w-full cursor-pointer flex-col rounded-sm
                                hover:shadow-md
                                @sm:w-[calc(50%-.25rem)]
                                @xl:w-[calc(33%-.55rem)]
                                @3xl:w-[calc(25%-.75rem)]
                              `}
                            >
                              <div
                                className={cn(
                                  `
                                    absolute top-2 left-2 flex aspect-square size-[30px] shrink-0 grow-0
                                    rounded-[1.25rem] bg-white p-[3px]
                                  `
                                )}
                              >
                                <RadioGroupItem
                                  id={id + "checkbox"}
                                  value={id}
                                  className={`
                                    flex aspect-square size-[24px] items-center justify-center border-2 bg-white
                                    *:aspect-square *:h-[10px] *:shrink-0 *:grow-0 *:rounded-full *:bg-primary
                                    [&_svg]:hidden
                                  `}
                                  onClick={() => trackClick(aContent.title ?? aContent.slug, a.searchParams, false)}
                                />
                              </div>

                              {aContent.image != "" && (
                                <Image
                                  src={aContent.image}
                                  alt={aContent.slug}
                                  unoptimized={true}
                                  width={qContent.imageSizes[0] ?? -1}
                                  height={qContent.imageSizes[1] ?? -1}
                                  className={cn("w-auto rounded-sm bg-white object-contain")}
                                />
                              )}
                              <div
                                className={cn("flex grow flex-col gap-1 rounded-b-sm bg-white p-2 pt-1", {
                                  "rounded-t-sm": aContent.image == "",
                                  "p-0": aContent.title == "" && aContent.description == "",
                                })}
                              >
                                {aContent.title && (
                                  <span
                                    className={cn("text-xl", {
                                      "indent-10": aContent.image == "",
                                      "mt-1":
                                        aContent.image == "" &&
                                        aContent.description == "" &&
                                        (aContent.longDescriptionHeader == "" || aContent.longDescriptionBody == ""),
                                      "my-1":
                                        aContent.description != "" ||
                                        (aContent.longDescriptionHeader != "" && aContent.longDescriptionBody != ""),
                                    })}
                                  >
                                    {aContent.title}
                                  </span>
                                )}
                                {aContent.description && (
                                  <p
                                    className={cn("text-base @sm:text-sm", {
                                      "ml-10": aContent.image == "" && aContent.title == "",
                                    })}
                                    dangerouslySetInnerHTML={{ __html: aContent.description }}
                                  />
                                )}
                                {aContent.longDescriptionHeader && aContent.longDescriptionBody && (
                                  <details
                                    className={cn("text-base text-gray-800 @sm:text-sm", {
                                      "ml-12 text-base!":
                                        aContent.image == "" && aContent.title == "" && aContent.description == "",
                                    })}
                                  >
                                    <summary>{qContent.longDescriptionHeader}</summary>
                                    <p
                                      className={"text-sm text-gray-600"}
                                      dangerouslySetInnerHTML={{ __html: qContent.longDescriptionBody }}
                                    />
                                  </details>
                                )}
                              </div>
                            </label>
                          )
                        })}
                      </RadioGroup>
                    )}
                </div>
                {!closed && (campaign.activeQuestions?.length ?? 0) > i + 1 && (
                  <div className={"mt-2 flex h-fit flex-row flex-nowrap gap-1 align-middle"} key={q.id + "_or"}>
                    <hr className={"border my-auto w-4 grow border-gray-400"} />
                    <span className={"text-sm font-light text-gray-500"}>{label_or}</span>
                    <hr className={"border my-auto w-full grow border-gray-400"} />
                  </div>
                )}
              </div>
            )
          })}
        {isSummaryPage && (
          // TODO handle closed
          <div className={"@container flex flex-col gap-4"}>
            <div
              className={cn("flex flex-row gap-4", { "justify-between": !closed, "flex-wrap justify-start": closed })}
            >
              {closed ? (
                <span className={"my-auto text-lg/tight font-bold"}>{label_breadcrumb_end}</span>
              ) : (
                <>{blok_summary_top}</>
              )}
              {showCloseButton && (
                <Button
                  size={closed ? "smNoGrow" : "iconNoGrow"}
                  variant={closed ? "default" : "ghost"}
                  type={"button"}
                  onClick={handleCloseClick}
                >
                  {closed ? label_btn_open : <XIcon className={"size-fit object-contain"} />}
                </Button>
              )}
            </div>
            {!closed && (
              <>
                <div
                  className={
                    "my-4 flex flex-row flex-wrap justify-center gap-4 text-sm text-gray-600 @sm:text-base @6xl:text-lg"
                  }
                >
                  {answeredQuestionsBreadcrumb.map((q, i) => (
                    <button
                      className={`
                        flex flex-row flex-wrap gap-1 rounded-lg bg-gray-200 px-2 py-1 text-wrap break-all hyphens-auto
                        hover:bg-primary-hover
                      `}
                      key={q.id}
                      onClick={() => {
                        i === 0
                          ? onResetClick()
                          : trackClick(q.question.slug, answeredQuestionsBreadcrumb[i - 1].searchParams)
                      }}
                    >
                      <p className={"font-light"}>
                        {q.question.slug}
                        {": "}
                      </p>
                      <p>{q.answer.slug}</p>
                      <CheckIcon className={"h-5 w-fit text-green-600 @sm:h-6 @6xl:h-7"} />
                    </button>
                  ))}
                </div>
                {blok_summary_bottom}
              </>
            )}
          </div>
        )}
        {!closed && (
          <div className={"flex flex-row justify-end gap-4 border-t border-gray-100 pt-4"}>
            {isSummaryPage ? (
              <Button
                size={"defaultNoGrow"}
                className={"flex flex-row gap-1"}
                onClick={onResetClick}
                disabled={showLoadingOnButton}
              >
                {showLoadingOnButton && <LoaderCircleIcon className={"motion-safe:animate-spin"} />}
                {label_btn_reset}
              </Button>
            ) : (
              <Button
                className={"flex flex-row gap-1"}
                size={"defaultNoGrow"}
                onClick={() => {
                  idToSearchParams[selectedAnswer] && trackClick(label_btn_confirm, idToSearchParams[selectedAnswer])
                }}
                disabled={showLoadingOnButton || selectedAnswer == "" || idToSearchParams[selectedAnswer] == undefined}
              >
                {showLoadingOnButton && <LoaderCircleIcon className={"motion-safe:animate-spin"} />}
                {label_btn_confirm}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
